import { useVisibleProperties } from "@hooks/useVisibleProperties";
import PropertyImagePreview from "@survey-components/propertiesPreview/PropertyImagePreview";
import PropertyPage from "@survey-components/propertiesPreview/PropertyPage";
import {
  createHeightBasedPages,
  PageData,
} from "@survey-preview/utils/heightPagination";
import { generatePages } from "@survey-preview/utils/pageGeneration";
import { processPropertyFields } from "@survey-preview/utils/propertyFieldUtils";
import { processLeftColumnContent } from "@survey-preview/utils/propertyProcessing";
import { Property } from "@survey-types/survey.property.types";

interface PropertyDetailsWithFilterProps {
  propertyFilter?: string;
}

interface ProcessedProperty {
  property: Property;
  index: number;
  pages: PageData[];
  propertyId: string;
}

/**
 * Custom component for property details with filtering
 * Recreates PropertyDetailsPreview logic but with filtered properties
 */
const PropertyDetailsWithFilter = ({
  propertyFilter,
}: PropertyDetailsWithFilterProps) => {
  const { survey, visibleProperties } = useVisibleProperties(propertyFilter);

  // Early return if no properties
  if (!survey || visibleProperties.length === 0) {
    return (
      <div className="p-4 text-center text-gray-500">
        {propertyFilter
          ? `Property ${propertyFilter} not found or hidden.`
          : "No visible properties found."}
      </div>
    );
  }

  // Simple boolean flags
  const isLandscape = survey?.pdfPaperSize === "Landscape";
  const includePhotos = Boolean(survey?.isIncludePhotos);

  // Process properties (same logic as PropertyDetailsPreview)
  const processedProperties: ProcessedProperty[] = visibleProperties.map(
    (property, index) => {
      const propertyFields = processPropertyFields(property, survey);
      const leftColumnContent = processLeftColumnContent(
        property,
        isLandscape,
        survey,
      );
      const propertyFieldPages = createHeightBasedPages(propertyFields, {
        isLandscape,
      });
      const pages = generatePages(
        leftColumnContent,
        propertyFieldPages,
        isLandscape,
      );

      return {
        property,
        index,
        pages,
        propertyId: property.propertyId,
      };
    },
  );

  // Render function for property details
  const renderPropertyDetails = (processedProperty: ProcessedProperty) => {
    const { property, index, pages } = processedProperty;

    return pages.map((page: PageData, pageIndex: number) => (
      <PropertyPage
        key={`property-${index}-page-${pageIndex}`}
        page={page}
        pageIndex={pageIndex}
        property={property}
        propertyIndex={index}
        survey={survey}
        mode="PDFPreview"
      />
    ));
  };

  return (
    <>
      {processedProperties.map((processedProperty) => {
        const { propertyId } = processedProperty;

        return (
          <div key={`property-${propertyId}`} className="flex flex-col">
            {renderPropertyDetails(processedProperty)}
            {includePhotos && (
              <PropertyImagePreview
                mode="PDFPreview"
                propertyFilter={propertyId}
              />
            )}
          </div>
        );
      })}
    </>
  );
};

export default PropertyDetailsWithFilter;
